import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StorageKey } from 'src/app/shared/constants/global-config.constant';
import { AppErrorHandler } from 'src/app/shared/services/app-error-handler';
import { environment } from 'src/environments/environment';
import { CommonService } from '../common/common.service';
import { DataPocketService } from '../data-pocket/data-pocket.service';
import { VersionService } from '../version/version.service';

interface TapRecord {
  t: string;
  tMs: number;
  /** For a click: tMs of the pointerdown that started this gesture (ng-select and
   *  friends react on mousedown, before the click exists). Otherwise own tMs. */
  gestureStartMs: number;
  type: string;
  x: number;
  y: number;
  target: string;
  stack: string[];
  /** The finger was over something clickable (selector match or cursor:pointer in the stack). */
  aimed: boolean;
  /** The loading/prepare overlay was up (or under the finger) — the page was visibly busy. */
  loading_at_tap: boolean;
  /** An NgZone turn fired within HANDLED_WINDOW_MS of the tap — some Angular handler ran. */
  handled: boolean;
  /** A DOM/text mutation, input or focus event landed within REACTION_WINDOW_MS of the
   *  gesture start. Tight on purpose: page-wide background activity must not be able
   *  to mask a dead tap. */
  reacted_fast: boolean;
  /** Filled in ~1.5s later: did the page react (handler ran / DOM or text mutation / input / navigation)? */
  reacted: boolean | null;
  route: string;
  swal_containers: number;
  body_class: string;
}

/**
 * Black-box recorder for the "page renders but ignores every tap" incident seen on
 * /payment-promptpay under both the CefSharp and Tauri shells. Records taps locally
 * (no network traffic) and, only on the freeze signature, (1) POSTs one evidence
 * snapshot to Pocketbase and (2) attempts an in-place repair that never navigates
 * or reloads — a reload would issue a new Omise charge and orphan a QR the
 * customer may have already paid.
 *
 * Freeze signature (v4): deadness is judged on CLICK events, not pointerdown.
 * The click is the moment handlers actually run, and a browser dispatches it
 * whether or not anything listens — so a frozen page still produces (unhandled)
 * clicks. Judging on pointerdown produced two false-alarm classes v3 shipped
 * with: a scroll gesture has a pointerdown but never becomes a click (39 of 73
 * dead taps in v3's first day were customers dragging the birthday dropdown),
 * and a firm press lifts the finger more than HANDLED_WINDOW_MS after
 * pointerdown, so the handler ran outside the watched window (17 of 73).
 * Disabled buttons produce no click at all, which also suppresses them.
 *
 * A click counts as DEAD only when the finger was over
 * something clickable (checked against the whole element stack under the point,
 * so an invisible overlay cannot hide the aim), the loading overlay was not up,
 * and the page showed no reaction within EVALUATE_AFTER_MS. A report fires on
 * DEAD_TAP_THRESHOLD dead taps on the same route within DEAD_TAP_WINDOW_MS,
 * spread over at least DEAD_TAP_MIN_SPAN_MS (a burst of QR-poking is not a
 * customer fighting a frozen page).
 *
 * "Reaction" learned the hard way (v1: 117 false alarms in hours — customers
 * poke the QR image, backgrounds, and ng-select dropdowns; v2: 67 — on-screen
 * keyboard taps update an input's VALUE, a DOM property MutationObserver cannot
 * see, and a validation no-op handler is healthy but looks dead): the primary
 * signal is now an NgZone turn within HANDLED_WINDOW_MS of the tap, i.e. some
 * Angular handler actually ran — even one that chose to do nothing. The window
 * is tight so the page's background timers (10s payment poll, 2s realtime
 * reconnect) almost never coincide and cannot mask a real freeze. DOM/text
 * mutations, input events, focus changes, and navigations also count as
 * reactions, judged from the start of the whole gesture (v5): ng-select opens
 * on mousedown, a text field reacts by focusing, and neither is visible at
 * click time otherwise.
 *
 * Custom clickable elements are recognised by `cursor: pointer` styling (which
 * Bootstrap's reboot already gives every enabled button) or by CLICKABLE_SELECTOR;
 * a new touch-anywhere surface belongs in that selector. /home's `.background`
 * is deliberately absent: its click handler is the hidden staff-menu counter, so
 * a customer tapping it gets no visible reaction even when everything is healthy.
 */
@Injectable({
  providedIn: 'root'
})
export class ClickDiagnosticService {

  private static readonly BUFFER_SIZE = 50;
  private static readonly EVALUATE_AFTER_MS = 1500;
  private static readonly HANDLED_WINDOW_MS = 150;
  private static readonly REACTION_WINDOW_MS = 300;
  private static readonly GESTURE_PAIR_MS = 1000;

  /** Top-level routed page selectors (direct children of .router-wrapper). Used by the
   *  stale-view repair; theme sub-components (app-*-portrait) are nested inside these
   *  and deliberately absent. */
  private static readonly PAGE_TAGS = [
    'app-home', 'app-verification', 'app-terms', 'app-product', 'app-welcome',
    'app-payment-option', 'app-payment-promptpay', 'app-payment-truemoney',
    'app-payment-link', 'app-payment-alipay', 'app-payment-not-supported',
    'app-payment-promo', 'app-payment-price', 'app-promo-type', 'app-payment-or-promo',
    'app-payment-success', 'app-payment-success-unlock', 'app-payment-failed',
    'app-payment-promo-text', 'app-payment-promo-qr', 'app-product-price',
    'app-error-camera', 'app-error-network', 'app-error-maintenance', 'app-error-down',
    'app-error-closed', 'app-error-fullstorage', 'app-error-printer-notfound',
    'app-error-notfound', 'app-error-cannot-get-price', 'app-report-logview',
    'app-config', 'app-config-set', 'app-exit', 'app-download-qrcode', 'app-hidden-menu'
  ];
  private static readonly DEAD_TAP_WINDOW_MS = 60 * 1000;
  private static readonly DEAD_TAP_THRESHOLD = 4;
  private static readonly DEAD_TAP_MIN_SPAN_MS = 10 * 1000;
  private static readonly REPORT_COOLDOWN_MS = 5 * 60 * 1000;
  private static readonly LOADING_STUCK_MS = 90 * 1000;

  private static readonly CLICKABLE_SELECTOR = [
    'button', 'a', 'input', 'select', 'textarea', 'label', '[role="button"]',
    // third-party widgets built from divs
    'ng-select', '.ng-option',
    // this app's custom clickables (audited from every (click) binding)
    '.consent-link', '.warning-caption', '.is-clickable',
    // touch-anywhere surfaces where every tap should visibly react
    '.welcome-image', '.warning-img', '.before.background'
  ].join(', ');

  private started = false;
  private buffer: TapRecord[] = [];
  private lastReportAt = 0;
  private lastMouseMoveAt = 0;
  private mouseMoveCount = 0;
  private lastMutationAt = 0;
  private lastNavigationAt = 0;
  private lastInputAt = 0;
  private lastFocusAt = 0;
  private lastZoneTurnAt = 0;
  /** Epoch ms when commonService.isLoading last flipped false -> true, or null while false. */
  private loadingSince: number | null = null;

  constructor(
    private httpClient: HttpClient,
    private ngZone: NgZone,
    private router: Router,
    private commonService: CommonService,
    private dataPocketService: DataPocketService,
    private versionService: VersionService
  ) { }

  start() {
    if (this.started) {
      return;
    }
    this.started = true;

    // A navigation is proof the page is alive.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.lastNavigationAt = Date.now());

    // A zone turn right after a tap means an Angular handler ran for it —
    // including a validation no-op that legitimately changes nothing on screen.
    this.ngZone.onMicrotaskEmpty.subscribe(() => this.markHandledTaps());

    // Capture phase on document sees every input the page receives, even when a
    // full-screen overlay is intercepting it — so the recording itself shows what
    // swallowed the tap. Registered outside the zone so pointer traffic and DOM
    // mutations never trigger change detection.
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('pointerdown', (e) => this.record('pointerdown', e), true);
      document.addEventListener('click', (e) => this.record('click', e), true);
      document.addEventListener('mousemove', () => this.onMouseMove(), true);
      document.addEventListener('input', () => { this.lastInputAt = Date.now(); this.markReactedTaps(); }, true);
      // Tapping a text field just focuses it — a reaction nothing else records.
      document.addEventListener('focusin', () => { this.lastFocusAt = Date.now(); this.markReactedTaps(); }, true);

      // A DOM change right after a tap counts as the page reacting — characterData
      // included, because Angular {{ }} bindings update text nodes. The recorder
      // itself never touches the DOM, so it cannot feed its own observer.
      const observer = new MutationObserver(() => { this.lastMutationAt = Date.now(); this.markReactedTaps(); });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });

      setInterval(() => this.tick(), 2000);
    });
  }

  private onMouseMove() {
    this.lastMouseMoveAt = Date.now();
    this.mouseMoveCount++;
  }

  /**
   * Called on every NgZone turn. Marks taps young enough that this turn was
   * plausibly their own handler running. Walks the buffer from the newest end
   * and stops at the first tap outside the window, so the frequent zone turns
   * stay cheap.
   */
  private markHandledTaps() {
    const now = Date.now();
    this.lastZoneTurnAt = now;
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const tap = this.buffer[i];
      if (now - tap.tMs > ClickDiagnosticService.HANDLED_WINDOW_MS) {
        break;
      }
      tap.handled = true;
    }
  }

  /**
   * Called on every DOM mutation / input / focus event. Marks taps whose gesture
   * started within REACTION_WINDOW_MS as reacted-to. Same cheap newest-first walk
   * as markHandledTaps.
   */
  private markReactedTaps() {
    const now = Date.now();
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const tap = this.buffer[i];
      if (now - tap.gestureStartMs > ClickDiagnosticService.REACTION_WINDOW_MS) {
        if (now - tap.tMs > ClickDiagnosticService.REACTION_WINDOW_MS) {
          break;
        }
        continue;
      }
      tap.reacted_fast = true;
    }
  }

  private tick() {
    if (this.commonService.isLoading) {
      if (this.loadingSince === null) {
        this.loadingSince = Date.now();
      }
    }
    else {
      this.loadingSince = null;
    }

    this.evaluatePendingTaps();

    // A leftover page needs no taps to be recognised: two routed pages under the
    // router wrapper, one of them not matching the route, is unambiguous. Catching
    // it here repairs it within a tick of appearing, usually before anyone taps.
    if (this.findStalePages().length > 0) {
      this.handleFreeze('stale-page');
      return;
    }

    if (this.countRecentDeadTaps() >= ClickDiagnosticService.DEAD_TAP_THRESHOLD) {
      this.handleFreeze('dead-taps');
    }
  }

  private record(type: string, e: MouseEvent) {
    const target = e.target instanceof Element ? e.target : null;
    const stack = document.elementsFromPoint(e.clientX, e.clientY);
    const now = Date.now();
    let gestureStartMs = now;
    if (type === 'click') {
      for (let i = this.buffer.length - 1; i >= 0; i--) {
        const prev = this.buffer[i];
        if (now - prev.tMs > ClickDiagnosticService.GESTURE_PAIR_MS) {
          break;
        }
        if (prev.type === 'pointerdown') {
          gestureStartMs = prev.tMs;
          break;
        }
      }
    }
    const entry: TapRecord = {
      t: new Date().toISOString(),
      tMs: now,
      gestureStartMs: gestureStartMs,
      type: type,
      x: Math.round(e.clientX),
      y: Math.round(e.clientY),
      target: this.describe(target),
      stack: stack.slice(0, 5).map(el => this.describe(el)),
      aimed: this.isAimedAtClickable(stack),
      loading_at_tap: this.commonService.isLoading
        || !!(target && target.closest('app-loading, app-prepare')),
      handled: false,
      reacted_fast: false,
      reacted: null,
      route: this.router.url,
      swal_containers: document.querySelectorAll('.swal2-container').length,
      body_class: document.body.className
    };
    this.buffer.push(entry);
    if (this.buffer.length > ClickDiagnosticService.BUFFER_SIZE) {
      this.buffer.shift();
    }
  }

  /**
   * Was the finger over something clickable? Checked against the full hit-test
   * stack, not just the event target: an invisible overlay catches the event,
   * but the button underneath is still in the stack — the customer aimed at it.
   */
  private isAimedAtClickable(stack: Element[]): boolean {
    // A disabled control is not expected to react. Checked on the event target
    // rather than per stack element because CEF 102 still dispatches clicks on
    // the children of a disabled button (newer Chromium suppresses them).
    const top = stack[0];
    if (top && top.closest('button[disabled], input[disabled], a.disabled, .disabled')) {
      return false;
    }
    for (const el of stack.slice(0, 8)) {
      if (el.matches(ClickDiagnosticService.CLICKABLE_SELECTOR)) {
        return true;
      }
      try {
        if (window.getComputedStyle(el).cursor === 'pointer') {
          return true;
        }
      }
      catch (err) { }
    }
    return false;
  }

  private describe(el: Element | null): string {
    if (!el) {
      return 'null';
    }
    const id = el.id ? `#${el.id}` : '';
    const cls = typeof el.className === 'string' && el.className ? `.${el.className.trim().split(/\s+/).slice(0, 4).join('.')}` : '';
    return `${el.tagName.toLowerCase()}${id}${cls}`;
  }

  /** Settle `reacted` for taps old enough to judge. */
  private evaluatePendingTaps() {
    const now = Date.now();
    for (const tap of this.buffer) {
      if (tap.reacted !== null || now - tap.tMs < ClickDiagnosticService.EVALUATE_AFTER_MS) {
        continue;
      }
      // Reactions are judged from the start of the whole gesture: widgets like
      // ng-select open on mousedown, before the click event exists. A zone turn
      // credited to the paired pointerdown counts for the click too. Mutations,
      // input and focus only count inside REACTION_WINDOW_MS (marked as they
      // happen); a navigation counts whenever it follows the gesture.
      tap.reacted = tap.handled
        || this.pairedPointerdownHandled(tap)
        || tap.reacted_fast
        || this.lastNavigationAt > tap.gestureStartMs;
    }
  }

  private pairedPointerdownHandled(tap: TapRecord): boolean {
    if (tap.type !== 'click' || tap.gestureStartMs === tap.tMs) {
      return false;
    }
    return this.buffer.some(p =>
      p.type === 'pointerdown' && p.tMs === tap.gestureStartMs && p.handled);
  }

  private isDead(tap: TapRecord): boolean {
    return tap.type === 'click'
      && tap.aimed
      && !tap.loading_at_tap
      && tap.reacted === false;
  }

  private countRecentDeadTaps(): number {
    const cutoff = Date.now() - ClickDiagnosticService.DEAD_TAP_WINDOW_MS;
    const route = this.router.url;
    const dead = this.buffer.filter(tap =>
      this.isDead(tap)
      && tap.tMs >= cutoff
      && tap.route === route
    );
    if (dead.length < ClickDiagnosticService.DEAD_TAP_THRESHOLD) {
      return 0;
    }
    // A half-second burst of pokes is not a customer fighting a frozen page.
    const span = dead[dead.length - 1].tMs - dead[0].tMs;
    if (span < ClickDiagnosticService.DEAD_TAP_MIN_SPAN_MS) {
      return 0;
    }
    return dead.length;
  }

  private handleFreeze(reason: string) {
    // What the customer was looking at — captured BEFORE the repair removes it.
    const visiblePage = this.visiblePageTag();

    // Repairs are idempotent and run on every detection; the report is rate-limited.
    const repairs = this.runRepairs();

    const now = Date.now();
    if (now - this.lastReportAt < ClickDiagnosticService.REPORT_COOLDOWN_MS) {
      return;
    }
    this.lastReportAt = now;
    this.sendReport(reason, visiblePage, repairs);
  }

  /**
   * The routed page actually painted on screen, as opposed to the one the router is
   * on: in the leftover-page freeze those differ, and the customer's complaint is
   * about the painted one. Hit-tests the screen centre; falls back to the stale
   * page (if any), then the route's own page.
   */
  private visiblePageTag(): string | null {
    try {
      const el = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
      const page = el && el.closest(ClickDiagnosticService.PAGE_TAGS.join(', '));
      if (page) {
        return page.tagName.toLowerCase();
      }
    }
    catch (err) { }
    const stale = this.findStalePages();
    if (stale.length > 0) {
      return stale[stale.length - 1].tagName.toLowerCase();
    }
    return this.expectedPageTag();
  }

  /**
   * Routed page elements under the router wrapper that do not belong to the current
   * route — but only while the page that DOES belong is also present, so this can
   * never name the active view.
   */
  private findStalePages(): Element[] {
    const wrapper = document.querySelector('.router-wrapper');
    const expectedTag = this.expectedPageTag();
    if (!wrapper || !expectedTag) {
      return [];
    }
    const pages = Array.from(wrapper.children)
      .filter(el => ClickDiagnosticService.PAGE_TAGS.indexOf(el.tagName.toLowerCase()) >= 0);
    if (!pages.some(el => el.tagName.toLowerCase() === expectedTag)) {
      return [];
    }
    return pages.filter(el => el.tagName.toLowerCase() !== expectedTag);
  }

  /**
   * In-place recovery only. Never navigates, never reloads, never touches the QR,
   * the charge, or the payment polling.
   */
  private runRepairs(): string[] {
    const repairs: string[] = [];

    // 1. Remove leftover SweetAlert2 containers. sweetalert2 only removes the
    //    container from an animationend listener; if that event is lost, an
    //    invisible full-viewport element keeps swallowing every tap. Containers
    //    whose popup is still visible are a real open dialog — left alone.
    document.querySelectorAll('.swal2-container').forEach(container => {
      if (!this.isSwalPopupVisible(container)) {
        container.remove();
        repairs.push(`removed stale swal container (${container.className})`);
      }
    });
    if (document.querySelectorAll('.swal2-container').length === 0) {
      Array.from(document.body.classList)
        .filter(c => c.startsWith('swal2-'))
        .forEach(c => {
          document.body.classList.remove(c);
          repairs.push(`removed body class ${c}`);
        });
    }

    // 2. Remove a stale page view left behind by a broken route transition — the
    //    root cause of the /payment-promptpay freeze (captured 28 times in 3 days
    //    on 2.12.3): the router navigated, the old page's component was destroyed
    //    (click handlers detached), but its DOM was never removed and sat on top
    //    of the live page. Only removes a known page element that does NOT match
    //    the current route while the matching one IS present — it can never touch
    //    the active view.
    const expectedTag = this.expectedPageTag();
    this.findStalePages().forEach(el => {
      el.remove();
      repairs.push(`removed stale page ${el.tagName.toLowerCase()} while on ${expectedTag}`);
    });

    // 3. Release a loading flag that has been up far longer than any legitimate
    //    call chain (retries included) can take. Inside the zone so the overlay
    //    actually leaves the DOM.
    if (this.commonService.isLoading
      && this.loadingSince !== null
      && Date.now() - this.loadingSince > ClickDiagnosticService.LOADING_STUCK_MS) {
      const stuckSeconds = Math.round((Date.now() - this.loadingSince) / 1000);
      this.ngZone.run(() => {
        this.commonService.isLoading = false;
      });
      this.loadingSince = null;
      repairs.push(`released isLoading stuck for ${stuckSeconds}s`);
    }

    return repairs;
  }

  /** The page tag the current route should be showing, or null when unsure. */
  private expectedPageTag(): string | null {
    const path = this.router.url.split('?')[0].split('#')[0].replace(/^\//, '');
    if (!path) {
      return null;
    }
    // Multi-segment routes first (payment-success/unlock), then the first segment
    // (download-qrcode/:id carries a record id in segment two).
    const full = `app-${path.replace(/\//g, '-')}`;
    if (ClickDiagnosticService.PAGE_TAGS.indexOf(full) >= 0) {
      return full;
    }
    const first = `app-${path.split('/')[0]}`;
    if (ClickDiagnosticService.PAGE_TAGS.indexOf(first) >= 0) {
      return first;
    }
    return null;
  }

  private isSwalPopupVisible(container: Element): boolean {
    const popup = container.querySelector('.swal2-popup');
    if (!popup) {
      return false;
    }
    if (popup.classList.contains('swal2-hide')) {
      return false;
    }
    const style = window.getComputedStyle(popup);
    return style.display !== 'none' && parseFloat(style.opacity) > 0.05;
  }

  private async sendReport(reason: string, visiblePage: string | null, repairs: string[]) {
    const payload = {
      branch_code: localStorage.getItem(StorageKey.BRANCH_CODE),
      environment: environment.production ? 'production' : 'development',
      route: this.router.url,
      // Top-level (not inside snapshot) so the Pocketbase admin can filter on them.
      visible_page: visiblePage,
      reason: reason,
      app_version: this.versionService.version,
      snapshot: {
        detected_at: new Date().toISOString(),
        rule: 'v6-structural',
        reason: reason,
        shell: this.detectShell(),
        user_agent: navigator.userAgent,
        repairs: repairs,
        recent_errors: AppErrorHandler.getRecent(),
        last_zone_turn_at: this.lastZoneTurnAt ? new Date(this.lastZoneTurnAt).toISOString() : null,
        dead_taps_in_window: this.buffer.filter(t => this.isDead(t)).length,
        last_mousemove_at: this.lastMouseMoveAt ? new Date(this.lastMouseMoveAt).toISOString() : null,
        mousemove_count: this.mouseMoveCount,
        last_mutation_at: this.lastMutationAt ? new Date(this.lastMutationAt).toISOString() : null,
        last_navigation_at: this.lastNavigationAt ? new Date(this.lastNavigationAt).toISOString() : null,
        is_loading: this.commonService.isLoading,
        swal_containers: document.querySelectorAll('.swal2-container').length,
        body_class: document.body.className,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        events: this.buffer.map(({ tMs, gestureStartMs, ...rest }) => rest)
      }
    };

    // Fire-and-forget: diagnostics must never disturb the customer flow, so no
    // retry toasts and every failure is swallowed. One re-login attempt covers
    // the token having expired on a booth that runs for days.
    try {
      await this.postReport(payload);
    }
    catch (err) {
      if (err && (err.status === 401 || err.status === 403)) {
        try {
          await this.dataPocketService.loginPocketbase();
          await this.postReport(payload);
        }
        catch (retryErr) {
          console.log('click diagnostic report failed after re-login', retryErr);
        }
      }
      else {
        console.log('click diagnostic report failed', err);
      }
    }
  }

  /**
   * Which desktop shell is hosting this page. The Tauri shell injects
   * __TAURI_INTERNALS__ into every frame; the old CefSharp shell's Chromium is
   * frozen at 102, which no other host reports.
   */
  private detectShell(): string {
    if ((window as any).__TAURI_INTERNALS__) {
      return 'tauri';
    }
    if (navigator.userAgent.indexOf('Chrome/102') >= 0) {
      return 'cefsharp';
    }
    return 'browser';
  }

  private postReport(payload: object): Promise<any> {
    return this.httpClient.post<any>(
      `${environment.pocket_endpoint}/api/collections/${environment.pocket_col_click_diag}/records`,
      payload,
      { headers: this.dataPocketService.getPocketbaseHeader() }
    ).toPromise();
  }

}
